import { getDecimalSeparator, parseLocaleFloat, formatLocaleNumber, maybeConvertAmount, isZeroDecimal, getValidPricePattern, PRICE_INPUT_PATTERN } from '../currency';

describe('getDecimalSeparator', () => {
  it('returns a comma for comma locales', () => {
    expect(getDecimalSeparator('de-DE')).toBe(',');
  });

  it('returns a dot for dot locales', () => {
    expect(getDecimalSeparator('en-US')).toBe('.');
  });

  it('falls back to a dot for a malformed locale tag instead of throwing', () => {
    expect(getDecimalSeparator('not a locale!')).toBe('.');
  });
});

describe('parseLocaleFloat', () => {
  it('parses a comma decimal without silently truncating', () => {
    // Regression: parseFloat('9,90') === 9, which under-prices PWYW fields.
    expect(parseLocaleFloat('9,90')).toBe(9.9);
    expect((parseLocaleFloat('9,90') * 100).toFixed(2)).toBe('990.00');
  });

  it('parses a dot decimal', () => {
    expect(parseLocaleFloat('9.90')).toBe(9.9);
  });

  it('accepts a comma regardless of locale (never inflates to 992)', () => {
    const result = parseLocaleFloat('99,2');
    expect(result).toBe(99.2);
    expect(result).not.toBe(992);
  });

  it('handles multi-separator grouping by disambiguating on the last separator', () => {
    expect(parseLocaleFloat('1.234,56')).toBe(1234.56);
    expect(parseLocaleFloat('1,234.56')).toBe(1234.56);
  });

  it('treats a grouping separator followed by 3 digits as grouping, not a decimal', () => {
    // Regression: "1,000" must be 1000, not 1 (a 1000x under-pricing).
    expect(parseLocaleFloat('1,000', 'en-US')).toBe(1000);
    expect(parseLocaleFloat('2,500', 'en-US')).toBe(2500);
    expect(parseLocaleFloat('10,000', 'en-US')).toBe(10000);
    expect(parseLocaleFloat('1.000', 'de-DE')).toBe(1000);
    expect(parseLocaleFloat('1,234,567', 'en-US')).toBe(1234567);
  });

  it('parses a plain integer', () => {
    expect(parseLocaleFloat('1000')).toBe(1000);
  });

  it('returns NaN for non-string input', () => {
    expect(parseLocaleFloat(undefined as any)).toBeNaN();
  });

  it('parses the Arabic decimal separator instead of deleting it', () => {
    // Regression: "9٫90" was stripped to 990 — a silent 100x overcharge.
    expect(parseLocaleFloat('9٫90')).toBe(9.9);
    expect(parseLocaleFloat('9٫90')).not.toBe(990);
  });

  it('parses Eastern Arabic-Indic digits', () => {
    expect(parseLocaleFloat('٩٫٩٠')).toBe(9.9);
    expect(parseLocaleFloat('١٬٠٠٠')).toBe(1000); // ٬ (U+066C) is grouping
    expect(parseLocaleFloat('١٢٣٤')).toBe(1234);
  });

  it('parses Persian digits', () => {
    expect(parseLocaleFloat('۹٫۹۰')).toBe(9.9);
    expect(parseLocaleFloat('۱۰۰')).toBe(100);
  });

  it('still tolerates whitespace grouping, apostrophe grouping and currency symbols', () => {
    expect(parseLocaleFloat('1 000,50')).toBe(1000.5);
    expect(parseLocaleFloat("1'000.50")).toBe(1000.5); // de-CH
    expect(parseLocaleFloat('€9,90')).toBe(9.9);
    expect(parseLocaleFloat('$1,234.56')).toBe(1234.56);
  });

  it('returns NaN for unknown characters instead of gluing digits together', () => {
    // Any unhandled separator-like character must fail loudly, never merge
    // "9" and "90" into a different amount.
    expect(parseLocaleFloat('9x90')).toBeNaN();
    expect(parseLocaleFloat('9;90')).toBeNaN();
    expect(parseLocaleFloat('9·90')).toBeNaN();
  });

  it('returns NaN for structurally invalid separator use, not a plausible wrong number', () => {
    // Doubled decimal points are a fat-finger, not "12.3".
    expect(parseLocaleFloat('1.2.3')).toBeNaN();
    expect(parseLocaleFloat('1,2,3,4')).toBeNaN();
    expect(parseLocaleFloat('1..50')).toBeNaN();
    // Valid grouped structures must keep working.
    expect(parseLocaleFloat('1.234,56')).toBe(1234.56);
    expect(parseLocaleFloat('1,234,567')).toBe(1234567);
  });

  it('rejects hyphens instead of truncating at them', () => {
    // parseFloat('9-90') would silently give 9; no price field takes a negative.
    expect(parseLocaleFloat('9-90')).toBeNaN();
    expect(parseLocaleFloat('-9.90')).toBeNaN();
  });

  it('tolerates a bare trailing separator mid-typing', () => {
    expect(parseLocaleFloat('9,')).toBe(9);
    expect(parseLocaleFloat('9.')).toBe(9);
  });

  it('rejects whitespace and currency symbols between digits instead of gluing', () => {
    // "9€90" is a real French convention for 9,90 — reading it as 990 is the
    // overcharge this whole fix exists to prevent. Edge symbols stay fine.
    expect(parseLocaleFloat('9€90')).toBeNaN();
    expect(parseLocaleFloat('9 90')).toBeNaN();
    expect(parseLocaleFloat('€9,90')).toBe(9.9);
  });
});

describe('PRICE_INPUT_PATTERN / parseLocaleFloat contract', () => {
  // The parser is the authority on the value; the pattern is a coarse filter in
  // front of it. It must never reject what the parser accepts, or a valid price
  // cannot be submitted. It may accept more, because it cannot know the locale.
  const pattern = new RegExp(`^(?:${PRICE_INPUT_PATTERN})$`, 'u');

  const accepted = ['9.90', '9,90', '9٫90', '٩٫٩٠', '۹٫۹۰', '9,', '.5', ',5', '٫5', '.99', '1000', '1,000', '١٬٠٠٠', '1,234,567', '1.234,56', '1,234.56', '1 000,50', "1'000.50", '€9,90', '$1,234.56'];
  const rejected = ['9x90', '9;90', '9-90', '-9.90', '1.2.3', '1,2,3,4', '1..50', '9 90', '9€90', '.', '.555'];

  it.each(accepted)('both accept %s', value => {
    expect(pattern.test(value)).toBe(true);
    expect(parseLocaleFloat(value, 'en-US')).not.toBeNaN();
  });

  it.each(rejected)('both reject %s', value => {
    expect(pattern.test(value)).toBe(false);
    expect(parseLocaleFloat(value, 'en-US')).toBeNaN();
  });

  it('never rejects what the parser accepts, for any locale', () => {
    for (const locale of ['en-US', 'de-DE', 'fa']) {
      for (const value of [...accepted, '1.000', '1.500', '9.999']) {
        if (!isNaN(parseLocaleFloat(value, locale))) {
          expect(pattern.test(value)).toBe(true);
        }
      }
    }
  });

  it('accepts everything formatLocaleNumber can emit', () => {
    for (const locale of ['en-US', 'de-DE', 'fa']) {
      expect(pattern.test(formatLocaleNumber(1234.56, locale))).toBe(true);
    }
  });
});

describe('parseLocaleFloat locale disambiguation', () => {
  // "1.500" is 1.5 to a US shopper and 1500 to a German one. Reading it by shape
  // alone turned a mistyped 9.999 into 9999 — the same silent 1000x this parser
  // exists to prevent, pointed the other way.
  it.each([
    ['1.500', 'en-US', NaN],
    ['1.500', 'de-DE', 1500],
    ['1,500', 'en-US', 1500],
    ['1,500', 'de-DE', NaN],
    ['9.999', 'en-US', NaN],
    ['12.345', 'en-US', NaN],
    ['1٫500', 'fa', NaN],
    ['1٬500', 'fa', 1500],
  ])('%s in %s', (value, locale, expected) => {
    const parsed = parseLocaleFloat(value, locale);
    isNaN(expected as number) ? expect(parsed).toBeNaN() : expect(parsed).toBe(expected);
  });

  it('reads a bare leading decimal, as the number input it replaced did', () => {
    // Chrome's type="number" accepted ".5" as 0.5, so rejecting it would be a
    // regression for anyone used to typing it.
    for (const locale of ['en-US', 'de-DE']) {
      expect(parseLocaleFloat('.5', locale)).toBe(0.5);
      expect(parseLocaleFloat(',5', locale)).toBe(0.5);
      expect(parseLocaleFloat('٫5', locale)).toBe(0.5);
      expect(parseLocaleFloat('.', locale)).toBeNaN();
      expect(parseLocaleFloat('.555', locale)).toBeNaN();
    }
  });

  it('leaves unambiguous amounts alone in every locale', () => {
    for (const locale of ['en-US', 'de-DE', 'fa']) {
      expect(parseLocaleFloat('9.90', locale)).toBe(9.9);
      expect(parseLocaleFloat('9,90', locale)).toBe(9.9);
      expect(parseLocaleFloat('1500', locale)).toBe(1500);
      expect(parseLocaleFloat('1,234.56', locale)).toBe(1234.56);
    }
  });
});

describe('getValidPricePattern', () => {
  it('returns the pattern where it compiles, so raw inputs can bind it', () => {
    expect(getValidPricePattern()).toBe(PRICE_INPUT_PATTERN);
  });

  it('returns undefined rather than a pattern the browser would choke on', () => {
    // An uncompilable `pattern` makes the browser skip validation for the whole
    // field, so the guard has to fail closed on the attribute, not throw.
    const original = RegExp;
    (global as any).RegExp = function () {
      throw new SyntaxError('unsupported');
    };

    try {
      expect(getValidPricePattern()).toBeUndefined();
    } finally {
      (global as any).RegExp = original;
    }
  });
});

describe('formatLocaleNumber', () => {
  it('uses the locale decimal separator so input matches what is displayed', () => {
    expect(formatLocaleNumber(9.9, 'de-DE')).toBe('9,9');
    expect(formatLocaleNumber(9.9, 'en-US')).toBe('9.9');
  });

  it('returns an empty string for NaN', () => {
    expect(formatLocaleNumber(NaN, 'en-US')).toBe('');
  });

  it('only ever emits "." or "," for non-Latin-separator locales', () => {
    // fa / ar-EG use "٫" (U+066B); emitting it would not round-trip through the parser.
    expect(formatLocaleNumber(9.9, 'fa')).toBe('9.9');
    expect(formatLocaleNumber(9.9, 'ar-EG')).toBe('9.9');
    // The formatted value must parse back to the original amount.
    expect(parseLocaleFloat(formatLocaleNumber(9.9, 'fa'))).toBe(9.9);
  });
});

describe('parse/format round trip', () => {
  it('is stable through convert -> format -> parse for a comma locale', () => {
    const cents = 990;
    const displayed = formatLocaleNumber(maybeConvertAmount(cents, 'eur'), 'de-DE'); // "9,9"
    expect(displayed).toBe('9,9');
    expect((parseLocaleFloat(displayed) * 100).toFixed(2)).toBe('990.00');
  });
});

describe('isZeroDecimal', () => {
  it('detects zero-decimal currencies case-insensitively', () => {
    expect(isZeroDecimal('JPY')).toBe(true);
    expect(isZeroDecimal('usd')).toBe(false);
  });
});
